/**
 * `STEP_UP_ACTIONS` — central registry of sensitive operations gated by
 * step-up OTP (`stepUpProcedure` middleware + the SPA's `useStepUpGate`
 * hook).
 *
 * Action strings are baked into:
 *   - The server middleware via `stepUpFounderProcedureFor(action)` — the
 *     procedure definition binds an exact action so callers can't bypass
 *     by passing a different action name from the wire.
 *   - The SPA's `useStepUpGate(action)` hook — drives `requestStepUpOtp`
 *     and `verifyStepUpOtp` with the matching action.
 *   - The session row's `step_up_action` column — middleware does an
 *     exact-string compare against the procedure's bound action.
 *
 * **Adding a new step-up-gated action** — three places to edit:
 *   1. Add an entry below (`FOO_BAR: 'foo.barBaz'`).
 *   2. Server: wrap the procedure in
 *        `stepUpFounderProcedureFor(STEP_UP_ACTIONS.FOO_BAR)`
 *      (or the non-founder `stepUpProcedureFor` variant).
 *   3. SPA: call
 *        `const gate = useStepUpGate(STEP_UP_ACTIONS.FOO_BAR);`
 *      and wrap the gated mutation in `gate.execute(thunk)`.
 *
 * The strings are namespaced like tRPC procedures (`'router.method'`)
 * for ad-hoc grep-ability in audit logs and analytics, but the format
 * is convention only — the middleware doesn't parse it.
 *
 * @see apps/api/src/trpc.ts — `stepUpProcedureFor` / `stepUpFounderProcedureFor`
 * @see apps/web/src/features/auth/use-step-up-gate.ts — SPA hook
 * @see 08-system-decisions.md §1 — sensitive operations + OTP cadence
 */
export const STEP_UP_ACTIONS = {
  /** Founder-only batch approve of `approval_pending` bills. */
  BILLS_BATCH_APPROVE: "bills.batchApprove",
  /** Vendor pay-to bank account / IFSC change (ADR-0008). */
  VENDORS_CHANGE_BANK_DETAILS: "vendors.changeBankDetails",
  /** Own (company) bank account number / IFSC change (ADR-0012 §1). */
  BANK_ACCOUNTS_CHANGE_BANK_DETAILS: "bankAccounts.changeBankDetails",
  /** Founder-only reversal of a recorded vendor payment. */
  VENDOR_PAYMENTS_VOID: "vendorPayments.void",
} as const;

export type StepUpAction = (typeof STEP_UP_ACTIONS)[keyof typeof STEP_UP_ACTIONS];

/** Runtime validator — accepts only registered action strings. */
export function isStepUpAction(value: unknown): value is StepUpAction {
  if (typeof value !== "string") return false;
  return (Object.values(STEP_UP_ACTIONS) as ReadonlyArray<string>).includes(value);
}
